import React, {FC, useEffect, useState} from 'react'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type ReportRow = {
  id: number
  certificateNameText?: string | null
  certificateName?: string | null
  certificateNameId?: number | null
  dateOfIssue?: string | null
  uploadedDate?: string | null
  vesselId?: number | null
  remarks?: string | null
  filePath?: string | null
}

type ReportLibraryName = {
  id: number
  name: string
}

type Payload = {
  id: number
  certificateName?: string
  certificateNameId?: number
  dateOfIssue?: string
  remarks?: string
  __file?: File | null
}

const toMonthValue = (value?: string | null) => {
  if (!value) return ''
  return value.substring(0, 7)
}

const fileNameFromPath = (path?: string | null) => {
  if (!path) return 'Report file'
  const normalized = path.replace(/\\/g, '/')
  return normalized.split('/').filter(Boolean).pop() || 'Report file'
}

const Module1ReportEditModal: FC<{
  isOpen: boolean
  report: ReportRow | null
  reportNames: ReportLibraryName[]
  vesselName?: string
  isSubmitting?: boolean
  onClose: () => void
  onViewFile: (report: ReportRow) => void
  onSubmit: (payload: Payload) => void
}> = ({isOpen, report, reportNames, vesselName, isSubmitting = false, onClose, onViewFile, onSubmit}) => {
  const [form, setForm] = useState({
    certificateNameId: '',
    certificateName: '',
    month: '',
    remarks: '',
  })
  const [newFile, setNewFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!report) return
    const name = report.certificateName || report.certificateNameText || ''
    const matchedName = reportNames.find((item) => item.name === name)
    setForm({
      certificateNameId: String(report.certificateNameId || matchedName?.id || ''),
      certificateName: name,
      month: toMonthValue(report.dateOfIssue || report.uploadedDate),
      remarks: report.remarks || '',
    })
    setNewFile(null)
    setErrors({})
  }, [report, reportNames])

  const validate = () => {
    const nextErrors: Record<string, string> = {}
    if (newFile) {
      const isPdfMime = newFile.type === 'application/pdf'
      const isPdfName = /\.pdf$/i.test(newFile.name || '')
      if (!isPdfMime && !isPdfName) {
        nextErrors.file = 'Only PDF files are allowed'
      }
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!report || !validate()) return
    onSubmit({
      id: report.id,
      certificateNameId: form.certificateNameId ? Number(form.certificateNameId) : undefined,
      certificateName: form.certificateName || undefined,
      dateOfIssue: form.month ? `${form.month}-01` : undefined,
      remarks: form.remarks || undefined,
      __file: newFile || undefined,
    })
  }

  if (!isOpen || !report) return null

  return (
    <div className='modal fade show d-block' tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <form onSubmit={handleSubmit}>
            <div className='modal-header'>
              <h5 className='modal-title'>Edit Report</h5>
              <button type='button' className='btn-close' onClick={onClose} disabled={isSubmitting} />
            </div>

            <div className='modal-body'>
              <div className='mb-6'>
                <label className='required fw-semibold fs-6 mb-2'>Select Vessel</label>
                <input
                  className='form-control form-control-solid'
                  value={vesselName || (report.vesselId ? `Vessel #${report.vesselId}` : '')}
                  disabled
                />
              </div>

              <div className='mb-6'>
                <label className='required fw-semibold fs-6 mb-2'>Report Name</label>
                <SearchableSelect
                  className='form-select form-select-solid'
                  value={form.certificateNameId}
                  onChange={(e) => {
                    const selectedId = e.target.value
                    const selectedItem = reportNames.find((item) => String(item.id) === selectedId)
                    setForm((prev) => ({
                      ...prev,
                      certificateNameId: selectedId,
                      certificateName: selectedItem?.name || prev.certificateName,
                    }))
                  }}
                  disabled
                >
                  <option value=''>{form.certificateName || 'Select Report Name'}</option>
                  {reportNames.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='mb-6'>
                <label className='fw-semibold fs-6 mb-2'>Remark</label>
                <textarea
                  className='form-control form-control-solid'
                  placeholder='Enter any remarks or notes (optional)'
                  rows={3}
                  value={form.remarks}
                  onChange={(e) => setForm((prev) => ({...prev, remarks: e.target.value}))}
                  disabled={isSubmitting}
                />
              </div>

              <div className='mb-6'>
                <label className='fw-semibold fs-6 mb-2'>Month</label>
                <input
                  type='month'
                  className='form-control form-control-solid'
                  value={form.month}
                  onChange={(e) => setForm((prev) => ({...prev, month: e.target.value}))}
                  disabled={isSubmitting}
                />
              </div>

              <div className='mb-6'>
                <label className='required fw-semibold fs-6 mb-2'>Existing File</label>
                <div className='d-flex justify-content-between align-items-center border rounded p-2'>
                  <div className='text-truncate pe-3'>
                    <span className='fw-semibold'>{fileNameFromPath(report.filePath)}</span>
                  </div>
                  <button type='button' className='btn btn-light-primary btn-sm' onClick={() => onViewFile(report)}>
                    View
                  </button>
                </div>
              </div>

              <div className='mb-6'>
                <label className='form-label fw-semibold fs-6 mb-2'>
                  Replace File (creates new revision) <span className='text-muted'>(PDF only, optional)</span>
                </label>
                <input
                  type='file'
                  className={`form-control form-control-solid ${errors.file ? 'is-invalid' : ''}`}
                  accept='.pdf,application/pdf'
                  onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                  disabled={isSubmitting}
                />
                {errors.file && <div className='invalid-feedback'>{errors.file}</div>}
                {newFile && !errors.file && (
                  <div className='form-text'>
                    Selected: {newFile.name} ({(newFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>
            </div>

            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={onClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Module1ReportEditModal
